import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, map, of, take } from 'rxjs';

import { UserRouteActionV1Model } from '@gfe/core/models/entities/implementations/user-route-action-v1';
import {
  UserInfrastructureV1Model,
  UserSiteV1Model
} from '@gfe/core/models/entities/implementations/user-site-v1';
import {
  EntitySelectorRequestModel,
  PlanFilter,
  PlanFilterOperator
} from '@gfe/core/models/entity/entity-selector-request.model';
import { FilterConfiguration } from '@gfe/shared/components/filter-query-builder/filter.models';
import { uidListFilter } from '@gfe/shared/utils/filter-condition.utils';

import { OccupationSeries } from '@features/components/visualization/occupation/models/occupation.model';
import {
  buildOccupationSeries,
  collectInfrastructureNames
} from '@features/components/visualization/occupation/occupation.builder';

import { AuxiliaryDataContext } from '../auxiliary-data/auxiliary-data.service';
import { SelectorsService } from '../selectors/selectors.service';
import { GenericEntityRegistryService } from '../user-models-registry/generic-entity-registry.service';

/**
 * Fetches what the occupation views chart: every stopover made at a site, by any route.
 *
 * Scoped by **site**, not by the table selection. A dock is contested between routes, so a
 * curve built from the routes a user happened to tick reports one truck at a bay that ten
 * trucks share. Route actions are the right axis for this: they carry the usages, they name
 * their own route, and the data endpoint filters them by `site_uid`, so the fetch costs what
 * the question costs rather than what the plan costs.
 *
 * Cached per site for the same reason `AuxiliaryDataService` is — a surface is re-entered
 * whenever the selection moves, and each move usually adds a site or two to a set already
 * resolved.
 */
@Injectable({ providedIn: 'root' })
export class OccupationDataService {
  private readonly selectorsService = inject(SelectorsService);
  private readonly registry = inject(GenericEntityRegistryService);

  /** cacheKey(context) -> siteUid -> the stopovers made there. */
  private readonly stopovers = new Map<string, Map<string, UserRouteActionV1Model[]>>();
  /** cacheKey(context) -> siteUid -> the site row, absent when the backend returned none. */
  private readonly sites = new Map<string, Map<string, UserSiteV1Model>>();
  /** cacheKey(context) -> infrastructure uid -> display name. */
  private readonly infrastructureNames = new Map<string, ReadonlyMap<string, string>>();

  /**
   * The series for exactly these sites, fetching only the ones not already held.
   *
   * The whole set is rebuilt on every call rather than merged incrementally: `usagesReported`
   * is a verdict over the set, so a series built from a subset would report a plan as
   * undetermined merely because the one site asked about happens to be idle.
   */
  seriesFor$(
    siteUids: string[],
    ctx: AuxiliaryDataContext
  ): Observable<OccupationSeries[]> {
    const wanted = [...new Set(siteUids)];
    if (wanted.length === 0) return of([]);

    const key = this.cacheKey(ctx);
    const knownStopovers = this.mapFor(this.stopovers, key);
    const missing = wanted.filter((uid) => !knownStopovers.has(uid));

    if (missing.length === 0 && this.infrastructureNames.has(key)) {
      return of(this.build(wanted, key));
    }

    return forkJoin({
      stopovers: this.fetchStopovers$(missing, ctx),
      sites: this.fetchSites$(missing, ctx),
      names: this.fetchInfrastructureNames$(ctx)
    }).pipe(
      take(1),
      map(({ stopovers, sites, names }) => {
        const knownSites = this.mapFor(this.sites, key);
        // Seeded empty first so a site the backend has nothing for is still recorded as
        // resolved; otherwise every later call refetches it.
        for (const uid of missing) knownStopovers.set(uid, []);
        for (const stopover of stopovers) {
          knownStopovers.get(stopover.site_uid)?.push(stopover);
        }
        for (const site of sites) knownSites.set(site.uid, site);
        this.infrastructureNames.set(key, names);

        return this.build(wanted, key);
      })
    );
  }

  /** Drops everything; call when a write may have changed what was fetched. */
  invalidate(): void {
    this.stopovers.clear();
    this.sites.clear();
    this.infrastructureNames.clear();
  }

  private build(siteUids: string[], key: string): OccupationSeries[] {
    const knownStopovers = this.mapFor(this.stopovers, key);
    const knownSites = this.mapFor(this.sites, key);

    return buildOccupationSeries(
      siteUids.flatMap((uid) => knownStopovers.get(uid) ?? []),
      siteUids
        .map((uid) => knownSites.get(uid))
        .filter((site): site is UserSiteV1Model => site !== undefined),
      this.infrastructureNames.get(key) ?? new Map(),
      new Set(siteUids)
    );
  }

  private fetchStopovers$(
    siteUids: string[],
    ctx: AuxiliaryDataContext
  ): Observable<UserRouteActionV1Model[]> {
    if (siteUids.length === 0) return of([]);
    return this.fetchAll$<UserRouteActionV1Model>(
      'UserRouteAction',
      (entityType) => this.siteFilter(siteUids, 'site_uid', entityType),
      ctx
    );
  }

  private fetchSites$(
    siteUids: string[],
    ctx: AuxiliaryDataContext
  ): Observable<UserSiteV1Model[]> {
    if (siteUids.length === 0) return of([]);
    return this.fetchAll$<UserSiteV1Model>(
      'UserSite',
      (entityType) => this.siteFilter(siteUids, 'uid', entityType),
      ctx
    );
  }

  /**
   * Infrastructure names, once per context and unfiltered — there are few of them, and
   * fetching the entity directly means a name no longer depends on a site or a route
   * declaring an auxiliary hop to it.
   */
  private fetchInfrastructureNames$(
    ctx: AuxiliaryDataContext
  ): Observable<ReadonlyMap<string, string>> {
    const cached = this.infrastructureNames.get(this.cacheKey(ctx));
    if (cached) return of(cached);

    return this.fetchAll$<UserInfrastructureV1Model>(
      'UserInfrastructure',
      undefined,
      ctx
    ).pipe(map((infrastructures) => collectInfrastructureNames(infrastructures)));
  }

  /**
   * The entity type is resolved from the registry rather than written here: it is the
   * dataspace's schema that names it, and a hard-coded guess would fail silently on a
   * deployment that names it otherwise.
   */
  private fetchAll$<T>(
    versionName: string,
    filters: ((entityType: string) => FilterConfiguration) | undefined,
    ctx: AuxiliaryDataContext
  ): Observable<T[]> {
    const entityType = this.registry.entityTypeFor(versionName);
    if (entityType === undefined) return of([]);

    const request: EntitySelectorRequestModel = {
      entityType,
      entityTypeVersion: versionName,
      dataSpaceId: ctx.dataSpaceId,
      planFilter: this.planFilter(ctx),
      filters: filters?.(entityType),
      view: 'FULL'
    };

    return this.selectorsService
      .selectAllData$<T>(request)
      .pipe(map((result) => result.items));
  }

  private siteFilter(
    siteUids: string[],
    field: string,
    entityType: string
  ): FilterConfiguration {
    return uidListFilter(`occupation-sites-${entityType}`, field, siteUids, entityType);
  }

  private planFilter(ctx: AuxiliaryDataContext): PlanFilter | undefined {
    if (ctx.planId == null) return undefined;
    return { id: ctx.planId, operator: ctx.planOperator ?? PlanFilterOperator.IN };
  }

  private mapFor<V>(cache: Map<string, Map<string, V>>, key: string): Map<string, V> {
    let byUid = cache.get(key);
    if (!byUid) {
      byUid = new Map();
      cache.set(key, byUid);
    }
    return byUid;
  }

  private cacheKey(ctx: AuxiliaryDataContext): string {
    return `${ctx.dataSpaceId}|${ctx.planId ?? ''}|${ctx.planOperator ?? ''}`;
  }
}
